import { useOp, type StudioUser } from '../api.ts';
import type { NavGroup } from '../nav.ts';
import type { Inbox as InboxData, InboxItem, InboxSection } from '../types.ts';
import { Link, useRouter } from '../router.tsx';
import { useChanges } from '../ui/changes.ts';
import { ErrorBox, Loading } from '../ui/kit.tsx';

interface SectionSpec {
  key: keyof InboxData;
  title: string;
  empty: string;
  more: { to: string; label: string };
  tone: 'review' | 'warn' | 'muted' | 'alert';
}

const SECTIONS: SectionSpec[] = [
  { key: 'pendingReview', title: 'Waiting for review', empty: 'Nothing waiting. New codes from sourcing land here.', more: { to: '/review', label: 'Open review queue' }, tone: 'review' },
  { key: 'expiringSoon', title: 'Expiring within 7 days', empty: 'No approved coupons expire this week.', more: { to: '/coupons?expiring=7', label: 'See all expiring' }, tone: 'warn' },
  { key: 'merchantsWithoutCode', title: 'Shown merchants without a current code', empty: 'Every shown merchant has at least one current code.', more: { to: '/merchants?filter=shown', label: 'Browse merchants' }, tone: 'muted' },
  { key: 'qaFlags', title: 'Open QA flags', empty: 'No open flags.', more: { to: '/coupons', label: 'Browse coupons' }, tone: 'alert' },
];

/** Today adapts to the person's roles: content work for content people, quick links to each other area they hold. */
export function Inbox({ user, groups }: { user: StudioUser; groups: readonly NavGroup[] }) {
  const capabilities = new Set(user.capabilities);
  const otherAreas = groups.filter((g) => g.label && g.id !== 'content');
  return (
    <section className="page">
      <header className="page-head">
        <div>
          <h1>Today</h1>
          <p className="muted">{capabilities.has('content.coupons:read') ? 'Work across your sites. Counts update as you go.' : 'Your areas in Studio.'}</p>
        </div>
      </header>
      {capabilities.has('content.coupons:read') ? <ContentToday canReview={capabilities.has('content.review:read')} /> : null}
      {otherAreas.length ? (
        <div className="inbox-grid today-areas">
          {otherAreas.map((group) => (
            <article key={group.id} className="card inbox-card tone-muted" aria-labelledby={`today-${group.id}`}>
              <header>
                <h2 id={`today-${group.id}`}>{group.label}</h2>
              </header>
              <ul className="inbox-list">
                {group.items.map((item) => (
                  <li key={item.to}>
                    <Link to={item.to} className="row-button">
                      <span className="truncate">{item.label}</span>
                      {item.placeholder ? <small className="muted">{item.placeholder}</small> : null}
                    </Link>
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      ) : null}
    </section>
  );
}

function ContentToday({ canReview }: { canReview: boolean }) {
  const changes = useChanges();
  const inbox = useOp<InboxData>('admin.inbox.today', {}, changes);
  const sections = SECTIONS.filter((spec) => canReview || spec.key !== 'pendingReview');
  return (
    <>
      {inbox.error ? <ErrorBox error={inbox.error} onRetry={inbox.reload} /> : null}
      {!inbox.data && inbox.loading ? <Loading /> : null}
      {inbox.data ? (
        <div className="inbox-grid">
          {sections.map((spec) => (
            <InboxCard key={spec.key} spec={spec} section={inbox.data![spec.key]} />
          ))}
        </div>
      ) : null}
    </>
  );
}

function InboxCard({ spec, section }: { spec: SectionSpec; section: InboxSection }) {
  const { setQuery, navigate } = useRouter();
  const open = (item: InboxItem) => {
    if (item.couponRef) setQuery({ coupon: item.couponRef });
    else if (item.merchantRef) navigate(`/merchants/${item.merchantRef}`);
  };
  return (
    <article className={`card inbox-card tone-${spec.tone}`} aria-labelledby={`inbox-${spec.key}`}>
      <header>
        <span className="big-count">{section.count}</span>
        <h2 id={`inbox-${spec.key}`}>{spec.title}</h2>
      </header>
      {section.items.length === 0 ? (
        <p className="muted">{spec.empty}</p>
      ) : (
        <ul className="inbox-list">
          {section.items.map((item, index) => (
            <li key={`${item.couponRef ?? item.merchantRef}-${index}`}>
              <button type="button" className="row-button" onClick={() => open(item)}>
                <span className="truncate">{item.title}</span>
                <small className="muted truncate">{item.detail.startsWith('Ends ') ? `Ends ${new Date(item.detail.slice(5)).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}` : item.detail}</small>
              </button>
            </li>
          ))}
        </ul>
      )}
      {section.count > 0 ? (
        <Link to={spec.more.to} className="card-more">
          {spec.more.label} →
        </Link>
      ) : null}
    </article>
  );
}
